import { type ReactNode, useState } from 'react';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { cn } from '@tsgecart/ui';
import { AdminSidebar } from './admin-sidebar.js';
import { AdminMobileNavigation } from './admin-mobile-navigation.js';
import { AdminTopbar } from './admin-topbar.js';

export interface AdminLayoutProps {
  children: ReactNode;
}

/**
 * The one shell every `/admin/*` page renders inside (Part 2). Reached only
 * through `RequireAuth` + `RequireAdmin` (see App.tsx) -- this component
 * itself performs no authorization check of its own; it is purely
 * presentational chrome around whatever page is already known to be
 * allowed here.
 */
export function AdminLayout({ children }: AdminLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <a
        href="#admin-main-content"
        className="focus:bg-brand sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-full focus:px-4 focus:py-2 focus:text-sm focus:font-semibold"
      >
        Skip to content
      </a>

      <div className="flex">
        <aside
          className={cn(
            'sticky top-0 hidden h-screen shrink-0 border-r border-gray-100 bg-white transition-[width] lg:block',
            collapsed ? 'w-[4.5rem]' : 'w-64',
          )}
        >
          <AdminSidebar collapsed={collapsed} />
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="focus-visible:ring-brand absolute -right-3 top-16 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2"
          >
            {collapsed ? (
              <PanelLeftOpen className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <PanelLeftClose className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </button>
        </aside>

        <AdminMobileNavigation open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

        <div className="min-w-0 flex-1">
          <AdminTopbar onOpenMobileNav={() => setMobileNavOpen(true)} />
          {children}
        </div>
      </div>
    </div>
  );
}
